/**
 * FSRS retrievability formula and effectiveMastery helper.
 *
 * Implements the FSRS-6 power-law forgetting curve (stated in full below) and
 * our effective-mastery deviation from it (see `effectiveMastery`).
 *
 * These are pure functions, used by the coach's per-subtopic snapshot and by
 * the client dashboard's mastery path for live `R(t, S)` and
 * `effectiveMastery` display — both read the same decay, so a learner's map
 * and their coach's view cannot disagree. They deliberately do NOT touch any
 * FSRS *update* math — that lands in Leg 1 (#403–#405).
 */

/** FSRS-6 retrievability constant (the `19/81` in the power-law). */
const FSRS_DECAY_CONSTANT = 19 / 81

/** A fully-novel item with no prior attempt is treated as fully retrievable. */
const NOVEL_RETRIEVABILITY = 1

/**
 * Power-law forgetting curve:
 *
 *     R(t, S) = (1 + (19/81) · t / S) ^ (-0.5)
 *
 * - `t` is days elapsed since the last attempt.
 * - `S` is the current stability in days.
 * - Returns 1 when `t ≤ 0` (no time has passed) — the formula naturally
 *   reduces to 1 at `t=0`, but the explicit check guards against negative
 *   clock drift in test data.
 * - Returns 1 when `S ≤ 0` (uninitialized stability) — degenerate input,
 *   surfaced as "not yet decayed" rather than `NaN`.
 *
 * Stability is defined as days-to-90% recall, so `R(S, S) ≈ 0.9` is a
 * useful sanity-check assertion (see the unit tests).
 */
export function retrievability(
  elapsedDays: number,
  stabilityDays: number,
): number {
  if (elapsedDays <= 0) return NOVEL_RETRIEVABILITY
  if (stabilityDays <= 0) return NOVEL_RETRIEVABILITY
  const ratio = (FSRS_DECAY_CONSTANT * elapsedDays) / stabilityDays
  return (1 + ratio) ** -0.5
}

/**
 * `effectiveMastery = mastery × R(t, S)`.
 *
 * A deliberate deviation from stock FSRS, which has no mastery ceiling and
 * schedules on retrievability alone. Our two layers:
 * - `mastery` is the slow-EMA ceiling (0–100), unchanged by time.
 * - `R(t, S)` is the current retrievability — what fraction of the ceiling
 *   the learner can actually reach today.
 *
 * Returns the same units as `mastery` (0–100).
 */
export function effectiveMastery(
  mastery: number,
  elapsedDays: number,
  stabilityDays: number,
): number {
  return mastery * retrievability(elapsedDays, stabilityDays)
}

/**
 * Days between two ISO timestamps. Returns 0 for null `from` (treated as
 * "no prior attempt" — caller will see novel-item retrievability).
 */
export function daysBetween(
  fromIso: string | null | undefined,
  toIso: string | Date,
): number {
  if (!fromIso) return 0
  const from = new Date(fromIso).getTime()
  const to = toIso instanceof Date ? toIso.getTime() : new Date(toIso).getTime()
  if (Number.isNaN(from) || Number.isNaN(to)) return 0
  const diffMs = to - from
  return diffMs > 0 ? diffMs / (1000 * 60 * 60 * 24) : 0
}
